'use client';

import React from 'react';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { Button } from 'rsuite';
import {
  AddFavoriteMutation,
  AddVariantToComparisonMutation,
  RemoveFavoriteMutation,
  RemoveVariantFromComparisonMutation,
  useAddFavoriteMutation,
  useAddVariantToComparisonMutation,
  useRemoveFavoriteMutation,
  useRemoveVariantFromComparisonMutation,
  useUserFavoritesQuery,
} from '@/graphql';
import { favoritesStore } from '@/zustand/favourite';
import comparisonStore from '@/zustand/comparison';
import useUserStore from '@/zustand/useUser';
import { useGuestStore } from '@/zustand/guestStore';
import { IoHeartOutline, IoHeartSharp } from 'react-icons/io5';
import IconCompare from '@/assets/icons/icon-compare.svg';
import IconCompareActive from '@/assets/icons/compare-active.svg';
import styles from './stickyHeader.module.scss';

type StickyHeaderProps = {
  id: string;
  isInCart: boolean;
  loadingBasket: boolean;
  loadingBuyNow: boolean;
  productName: string | undefined;
  productPrice: number | undefined;
  productImage: string | undefined;
  showHeader: boolean;
  onBasketClick?: () => void;
  onBuyNowClick?: () => void;
};

const StickyHeader: React.FC<StickyHeaderProps> = ({
  id,
  isInCart,
  loadingBasket,
  loadingBuyNow,
  productImage,
  productName,
  productPrice,
  showHeader,
  onBasketClick,
  onBuyNowClick,
}) => {
  const t = useTranslations('General');
  const favorites = favoritesStore(e => e.favorites);
  const setFavorites = favoritesStore(e => e.setFavorites);
  const setComparison = comparisonStore(e => e.setComparison);
  const comparison = comparisonStore(e => e.comparison);
  const userData = useUserStore((user: any) => user?.user);
  const isLoggedIn = Boolean(userData?.id);
  const guestFavoriteListArray = useGuestStore(state => state.favoriteList);

  const {
    data: favoriteData,
    loading,
    refetch: refetchFavoriteData,
  } = useUserFavoritesQuery({
    variables: { first: 10 },
    context: {
      requiresAuth: true,
    },
  });

  const isFavorite = (id: any): boolean => {
    if (isLoggedIn) {
      return Array.isArray(favoriteData?.me?.favorites?.edges)
        ? favoriteData!.me!.favorites!.edges!.some(
            (favorite: any) => favorite?.node?.id === id
          )
        : false;
    } else {
      return Array.isArray(guestFavoriteListArray)
        ? guestFavoriteListArray.includes(id)
        : false;
    }
  };

  const [create] = useAddFavoriteMutation({
    onCompleted: (data: AddFavoriteMutation) => {
      if (data?.addFavorite?.errors?.length === 0) {
        setFavorites(data?.addFavorite?.user?.favorites?.edges || []);
      }
    },
    context: {
      requiresAuth: true,
    },
  });

  const [remove] = useRemoveFavoriteMutation({
    onCompleted: (data: RemoveFavoriteMutation) => {
      if (data?.removeFavorite?.errors?.length === 0) {
        setFavorites(data?.removeFavorite?.user?.favorites?.edges || []);
      }
    },
    context: {
      requiresAuth: true,
    },
  });

  const addToFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        const result = await create({
          variables: {
            variant: id,
          },
        });
        refetchFavoriteData();
      } else {
        useGuestStore.getState().addToFavorite(id);
      }
    } catch (error) {
      console.error('Error on adding to favorite: ', error);
    }
  };

  const removeFromFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        await remove({
          variables: {
            variant: id,
          },
        });
        refetchFavoriteData();
      } else {
        useGuestStore.getState().removeFromFavorite(id);
      }
    } catch (error) {
      console.error('Error on removing from favorite: ', error);
    }
  };

  const isInComparison = Array.isArray(comparison)
    ? comparison.some((item: any) => item.id === id)
    : false;

  const [createComparison, createComparisonOps] =
    useAddVariantToComparisonMutation({
      onCompleted: (data: AddVariantToComparisonMutation) => {
        if (data?.addVariantToComparison?.errors?.length === 0) {
          setComparison(data?.addVariantToComparison?.user?.comparison);
        }
      },
      context: {
        requiresAuth: true,
      },
    });

  const [removeComparison, removeComparisonOps] =
    useRemoveVariantFromComparisonMutation({
      onCompleted: (data: RemoveVariantFromComparisonMutation) => {
        if (data?.removeVariantFromComparison?.errors?.length === 0) {
          setComparison(data.removeVariantFromComparison?.user?.comparison);
        }
      },
      context: {
        requiresAuth: true,
      },
    });

  const addToComparison = async (id: any) => {
    if (!isLoggedIn) {
      useGuestStore.getState().addToComparison(id);
    } else {
      await createComparison({
        variables: {
          variant: id,
        },
      });
    }
  };

  const removeFromComparison = async (id: any) => {
    if (isLoggedIn) {
      await removeComparison({
        variables: {
          variant: id,
        },
      });
    } else {
      useGuestStore.getState().removeFromComparison(id);
    }
  };
  return (
    <div className={`${styles.container} ${showHeader ? styles.visible : ''}`}>
      <div className={styles.wrapper}>
        <div className={styles.aboutProduct}>
          {productImage && (
            <div className={styles.productImage}>
              <Image
                src={productImage as string}
                alt={productName ?? 'product-image'}
                fill
                style={{ objectFit: 'contain' }}
              />
            </div>
          )}
          <div className={styles.textContainer}>
            {productName && (
              <h3 className={styles.productName}>{productName}</h3>
            )}
            {productPrice && (
              <h5 className={styles.productPrice}>
                {productPrice.toLocaleString('fr-FR').replace(/\u00A0/g, ' ')}{' '}
                {t('price')}
              </h5>
            )}
          </div>
        </div>
        <div className={styles.actions}>
          <div className={styles.links}>
            <Button
              onClick={() =>
                isInComparison ? removeFromComparison(id) : addToComparison(id)
              }
              type="button"
              className={styles.icon}
            >
              {isInComparison ? <IconCompareActive /> : <IconCompare />}
            </Button>
            <motion.div
              whileTap={{ scale: 2 }}
              transition={{ duration: 0.5 }}
              onClick={() =>
                isFavorite(id) ? removeFromFavorite(id) : addToFavorite(id)
              }
              style={{
                cursor: 'pointer',
              }}
            >
              {isFavorite(id) ? (
                <Button type="button" className={styles.icon}>
                  <IoHeartSharp size={25} color="red" />
                </Button>
              ) : (
                <Button type="button" className={styles.icon}>
                  <IoHeartOutline size={25} />
                </Button>
              )}
            </motion.div>
          </div>
          <div className={styles.links}>
            <Button
              onClick={onBasketClick}
              className={`${styles.button} ${styles.buttonYellow}`}
              loading={loadingBasket}
            >
              {isInCart ? t('in-cart') : t('to-cart')}
            </Button>
            <Button
              onClick={onBuyNowClick}
              className={`${styles.button} ${styles.buttonGray}`}
              loading={loadingBuyNow}
            >
              {t('buy-now')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyHeader;
